<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const statusList = [
  {
    status: 'Draft',
    color: 'default',
    icon: 'ri-draft-line',
    desc: 'PO baru dibuat, belum dikirim ke supplier. Masih bisa diedit, dihapus permanen, atau dibatalkan.',
    aksi: 'Edit PO / Hapus PO / Kirim ke Supplier / Batalkan',
  },
  {
    status: 'Dikirim',
    color: 'info',
    icon: 'ri-send-plane-line',
    desc: 'PO sudah dikirim ke supplier. Tidak bisa diedit lagi.',
    aksi: 'Buat Receiving / Batalkan PO',
  },
  {
    status: 'Sebagian',
    color: 'warning',
    icon: 'ri-git-branch-line',
    desc: 'Barang sudah sebagian datang, tapi belum semua. Masih menunggu sisa kiriman.',
    aksi: 'Buat Receiving tambahan',
  },
  {
    status: 'Selesai',
    color: 'success',
    icon: 'ri-checkbox-circle-line',
    desc: 'Semua barang sudah diterima. PO dianggap selesai.',
    aksi: 'Lihat detail saja',
  },
  {
    status: 'Dibatalkan',
    color: 'error',
    icon: 'ri-close-circle-line',
    desc: 'PO dibatalkan. Tidak ada penerimaan barang dari PO ini.',
    aksi: 'Tidak ada aksi lanjutan',
  },
]

const headerFields = [
  { field: 'Supplier', required: true, desc: 'Pilih supplier yang Anda pesan barangnya. Hanya supplier aktif yang muncul.', example: '"CV Agro Nusantara"' },
  { field: 'Estimasi Tiba', required: false, desc: 'Perkiraan tanggal barang tiba. Berguna untuk perencanaan stok.', example: '"15 Juli 2026"' },
  { field: 'Catatan', required: false, desc: 'Pesan atau instruksi khusus untuk supplier. Opsional.', example: '"Minta kopi Arabika yang baru roast, jangan yang lama"' },
]

const itemFields = [
  { field: 'Bahan Baku', required: true, desc: 'Pilih bahan baku yang ingin dipesan. Hanya bahan yang sudah terdaftar di sistem.', example: '"Kopi Arabika Gayo"' },
  { field: 'Satuan Pembelian', required: true, desc: 'Satuan sesuai yang tertulis di nota/faktur supplier — boleh satuan dasar bahan (mis. Gram) atau satuan turunannya yang lebih besar (mis. Kilogram), kalau sudah dihubungkan di menu Satuan.', example: '"Kilogram" (untuk bahan bersatuan dasar Gram)' },
  { field: 'Qty', required: true, desc: 'Jumlah yang dipesan, dalam Satuan Pembelian yang dipilih di atas — bukan dalam satuan dasar.', example: '"1" (artinya 1 Kilogram, bukan 1 gram)' },
  { field: 'Total Harga Pembelian', required: true, desc: 'Total uang yang dibayar untuk baris item ini (Rupiah) — bukan harga per satuan. Sama seperti angka "Subtotal" yang tertulis di nota supplier.', example: '"18000" (Rp18.000 untuk 1 Kg)' },
  { field: 'HPP per Satuan Dasar', required: false, desc: 'Read-only, informasi saja — dihitung otomatis realtime (Total Harga ÷ Qty, dikonversi ke satuan dasar bahan). Ini yang dipakai sistem untuk update rata-rata HPP bahan saat barang diterima.', example: '"Rp 18/gram" (otomatis dari 18.000 ÷ 1.000 gram)' },
]

const faq = [
  {
    q: 'Apakah wajib buat Purchase Order sebelum barang datang?',
    a: 'Tidak wajib. Anda bisa langsung buat Receiving (penerimaan barang) tanpa PO — ini disebut "Direct Receiving". Namun sangat disarankan pakai PO agar ada catatan resmi apa yang dipesan vs apa yang datang, terutama untuk pembelian besar atau rutin.',
  },
  {
    q: 'Saya sudah kirim PO tapi supplier minta perubahan. Bisa diedit?',
    a: 'Tidak bisa edit PO yang sudah berstatus "Dikirim". Solusinya: batalkan PO tersebut, lalu buat PO baru dengan data yang sudah direvisi. Pembatalan PO tidak menghapus data, hanya mengubah statusnya menjadi "Dibatalkan".',
  },
  {
    q: 'Apa bedanya tombol "Hapus PO" dengan "Batalkan PO"?',
    a: '"Hapus PO" menghapus data PO secara permanen — hanya tersedia untuk PO berstatus Draft yang belum pernah dikirim ke supplier, cocok untuk PO yang salah buat atau cuma coba-coba. "Batalkan PO" mengubah status jadi "Dibatalkan" tapi datanya tetap tersimpan sebagai riwayat — dipakai untuk PO yang sudah dikirim tapi pesanannya tidak jadi. Begitu PO berstatus "Dikirim", tombol Hapus tidak akan muncul lagi, hanya tombol Batalkan.',
  },
  {
    q: 'Bisa pesan beberapa bahan sekaligus dalam satu PO?',
    a: 'Bisa. Dalam satu PO Anda bisa tambah banyak baris item. Klik tombol "+ Tambah Item" untuk menambah baris baru. Satu PO bisa berisi puluhan item sekaligus.',
  },
  {
    q: 'Apakah total harga PO otomatis dihitung?',
    a: 'Ya, sebagian. Anda mengisi "Total Harga Pembelian" per item langsung (sesuai nota supplier), lalu sistem menjumlahkan semua item menjadi Grand Total PO secara otomatis. Yang dihitung otomatis oleh sistem adalah kolom "HPP per Satuan Dasar" — itu murni informasi, Anda tidak perlu menghitungnya sendiri.',
  },
  {
    q: 'Kenapa pilihan Satuan Pembelian saya terbatas, tidak semua satuan muncul?',
    a: 'Untuk mencegah kesalahan konversi, sistem hanya menampilkan satuan dasar bahan tersebut, atau satuan turunan yang sudah Anda hubungkan ke satuan dasarnya di menu Satuan (mis. Kilogram dihubungkan ke Gram dengan faktor 1000). Kalau satuan yang Anda mau beli (mis. Liter) belum dihubungkan, buka menu Satuan dan hubungkan dulu sebelum kembali ke form PO.',
  },
  {
    q: 'Bagaimana jika barang yang datang berbeda jumlahnya dari yang dipesan di PO?',
    a: 'Tidak masalah. Saat buat Receiving, Anda bisa mengisi jumlah yang benar-benar datang — tidak harus sama dengan jumlah di PO. Sistem akan catat selisihnya dan status PO akan jadi "Sebagian" jika belum semua barang datang.',
  },
  {
    q: 'Apakah PO bisa dicetak atau dikirim ke supplier?',
    a: 'Saat ini fitur cetak/export PO belum tersedia langsung. Anda bisa screenshot halaman detail PO untuk dikirim ke supplier via WhatsApp atau email. Fitur cetak PDF direncanakan di versi mendatang.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <div class="d-flex align-center gap-2 mb-3">
      <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
        User Guide
      </RouterLink>
      <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
      <RouterLink to="/userguide/pembelian" class="text-decoration-none text-medium-emphasis text-body-2">
        Pembelian
      </RouterLink>
      <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
      <span class="text-body-2 font-weight-medium">Purchase Order</span>
    </div>

    <!-- Header -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="warning" variant="tonal" size="64" rounded="lg" class="me-4">
            <VIcon icon="ri-file-list-3-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">Purchase Order (PO)</h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Buat surat pesanan resmi ke supplier sebelum barang datang
            </p>
          </div>
        </div>
        <VAlert type="info" variant="tonal">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda membuat, mengelola, dan memantau Purchase Order (PO) — yaitu catatan resmi
          bahan baku apa yang Anda pesan, dari supplier mana, dan berapa harganya.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="warning" />
            Apa itu Purchase Order?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Purchase Order (PO)</strong> adalah dokumen resmi yang berisi daftar barang
              yang Anda pesan dari supplier, lengkap dengan jumlah dan harga yang disepakati.
              Ibaratnya seperti <em>"surat belanja resmi"</em> yang bisa Anda kirimkan ke supplier.
            </p>

            <VAlert type="success" variant="tonal" class="mb-3">
              <strong>Contoh nyata:</strong>
              Setiap Senin Anda rutin beli bahan dari CV Agro. Biasanya Anda chat WhatsApp:
              <em>"Pak, minta 5 kg Arabika, 2 kg Robusta sama 10 liter susu ya"</em>.
              Dengan PO, pesanan itu tercatat resmi di sistem lengkap dengan harga —
              dan riwayatnya tersimpan permanen untuk referensi Anda.
            </VAlert>

            <p class="text-body-2 mb-0">
              PO juga jadi <strong>acuan penerimaan barang</strong> — saat barang datang,
              Anda tinggal cek apakah sesuai dengan yang dipesan di PO atau tidak.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Smart HPP Calculator -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-calculator-line" class="me-2" color="success" />
            ✨ Smart HPP Calculator — Beli Sesuai Nota, Sistem yang Hitung
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Item PO tidak lagi meminta "harga per satuan dasar" yang harus Anda hitung
              sendiri. Anda cukup isi <strong>Satuan Pembelian</strong> (sesuai nota supplier),
              <strong>Qty</strong>, dan <strong>Total Harga Pembelian</strong> — sistem otomatis
              menghitung <strong>HPP per Satuan Dasar</strong> secara realtime, memakai
              konversi satuan yang sama dengan fitur Stok Awal di Bahan Baku.
            </p>

            <VAlert type="success" variant="tonal" class="mb-3">
              <strong>Contoh:</strong> Bahan "Kopi Arabika" satuan dasarnya <strong>Gram</strong>,
              tapi Anda beli di supplier dalam <strong>Kilogram</strong>. Isi Satuan Pembelian
              = Kilogram, Qty = 1, Total Harga Pembelian = Rp 18.000. Sistem otomatis
              menampilkan <strong>HPP per Gram = Rp 18</strong> — dan saat barang diterima
              (Receiving dikonfirmasi), stok bertambah <strong>1.000 gram</strong> (bukan 1),
              bukan Kilogram, sesuai satuan dasar bahan tersebut.
            </VAlert>

            <VAlert type="warning" variant="tonal" class="mb-0 text-body-2">
              <strong>Satuan Pembelian yang bisa dipilih terbatas.</strong>
              Anda hanya bisa memilih satuan dasar bahan itu sendiri, atau satuan
              turunan yang sudah dihubungkan ke satuan dasar tersebut di menu
              <RouterLink to="/userguide/produk-inventory/satuan">Satuan</RouterLink>
              (mis. Kilogram → Gram). Kalau satuan yang Anda pilih tidak cocok
              untuk bahan tersebut, sistem akan menolak dan meminta Anda pilih ulang.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Status PO -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-git-branch-line" class="me-2" color="info" />
            Status Purchase Order — Apa Artinya?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Setiap PO punya status yang berubah mengikuti alur proses. Ini penjelasannya:
            </p>
            <VRow>
              <VCol
                v-for="s in statusList"
                :key="s.status"
                cols="12"
                sm="6"
                lg="true"
              >
                <VCard variant="tonal" :color="s.color === 'default' ? 'grey' : s.color" class="h-100">
                  <VCardText class="pa-3">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VIcon :icon="s.icon" size="20" />
                      <VChip :color="s.color === 'default' ? 'grey' : s.color" size="small" label>
                        {{ s.status }}
                      </VChip>
                    </div>
                    <p class="text-body-2 mb-2">{{ s.desc }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      <strong>Aksi tersedia:</strong> {{ s.aksi }}
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Buat PO -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-add-circle-line" class="me-2" color="success" />
            Cara Membuat Purchase Order Baru
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-0">
              <li class="mb-4">
                Buka menu <strong>Pembelian → Purchase Order</strong> dari sidebar kiri
              </li>

              <li class="mb-4">
                Klik tombol <strong>"+ Buat PO"</strong> di pojok kanan atas
              </li>

              <li class="mb-4">
                <p class="mb-2">Isi <strong>informasi PO (bagian atas form):</strong></p>
                <VTable>
                  <thead>
                    <tr>
                      <th style="min-width: 140px">Field</th>
                      <th style="min-width: 70px">Wajib?</th>
                      <th>Penjelasan & Contoh</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="f in headerFields" :key="f.field">
                      <td><strong>{{ f.field }}</strong></td>
                      <td>
                        <VChip :color="f.required ? 'error' : 'default'" size="x-small" variant="tonal">
                          {{ f.required ? '✅ Wajib' : '❌ Opsional' }}
                        </VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        {{ f.desc }}
                        <br>
                        <span class="text-caption text-medium-emphasis">Contoh: {{ f.example }}</span>
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </li>

              <li class="mb-4">
                <p class="mb-2">
                  Isi <strong>item pesanan (tabel bawah)</strong> — 1 baris = 1 bahan baku:
                </p>
                <VTable>
                  <thead>
                    <tr>
                      <th style="min-width: 130px">Kolom</th>
                      <th style="min-width: 70px">Wajib?</th>
                      <th>Penjelasan & Contoh</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="f in itemFields" :key="f.field">
                      <td><strong>{{ f.field }}</strong></td>
                      <td>
                        <VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        {{ f.desc }}
                        <br>
                        <span class="text-caption text-medium-emphasis">Contoh: {{ f.example }}</span>
                      </td>
                    </tr>
                  </tbody>
                </VTable>

                <VAlert type="info" variant="tonal" class="mt-3 text-body-2">
                  <strong>Mau tambah item lagi?</strong>
                  Klik <strong>"+ Tambah Item"</strong> untuk menambah baris baru.
                  Tidak ada batasan jumlah item dalam 1 PO.
                  Subtotal dan Grand Total dihitung otomatis saat Anda isi angka.
                </VAlert>
              </li>

              <li class="mb-4">
                Klik <strong>"Simpan PO"</strong>. PO tersimpan dengan status <strong>Draft</strong>.
              </li>

              <li>
                PO masih bisa diedit selama masih berstatus <strong>Draft</strong>.
                Saat sudah siap, lanjut ke langkah berikut: kirim ke supplier.
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Kirim ke Supplier -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-send-plane-line" class="me-2" color="info" />
            Cara Mengirim PO ke Supplier
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Setelah PO selesai dibuat dan sudah Anda cek ulang, langkah berikutnya adalah
              mengubah statusnya menjadi "Dikirim" agar sistem tahu PO ini sudah aktif.
            </p>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">Buka halaman <strong>detail PO</strong> yang ingin dikirim</li>
              <li class="mb-2">Klik tombol <strong>"Kirim ke Supplier"</strong> (biru)</li>
              <li class="mb-2">Konfirmasi di dialog yang muncul</li>
              <li>Status PO berubah menjadi <strong>"Dikirim"</strong> ✅</li>
            </ol>
            <VAlert type="warning" variant="tonal" class="text-body-2">
              <strong>⚠️ Penting:</strong>
              Setelah status berubah ke "Dikirim", PO <strong>tidak bisa diedit lagi</strong>.
              Pastikan semua data sudah benar sebelum klik kirim.
              Jika ada kesalahan, Anda perlu batalkan PO dan buat PO baru.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Edit, Hapus & Batalkan -->
    <VRow class="mb-4">
      <VCol cols="12" md="4">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-edit-line" class="me-2" color="warning" />
            Edit PO (Hanya Draft)
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              PO hanya bisa diedit selama masih berstatus <strong>Draft</strong>.
            </p>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">Buka detail PO</li>
              <li class="mb-2">Klik tombol <strong>"Edit"</strong></li>
              <li class="mb-2">Ubah data (supplier, item, qty, harga, catatan)</li>
              <li>Klik <strong>"Simpan Perubahan"</strong></li>
            </ol>
            <VAlert type="info" variant="tonal" density="compact" class="text-body-2">
              Tombol Edit tidak muncul jika PO sudah Dikirim, Selesai, atau Dibatalkan.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="4">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-delete-bin-line" class="me-2" color="error" />
            Hapus PO (Hanya Draft)
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Gunakan ini untuk PO yang salah buat atau cuma coba-coba dan belum pernah
              dikirim ke supplier — datanya benar-benar dihapus, tidak disimpan sebagai riwayat.
            </p>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">Buka detail (atau daftar) PO</li>
              <li class="mb-2">Klik ikon/tombol <strong>"Hapus PO"</strong> (merah)</li>
              <li>Konfirmasi di dialog yang muncul</li>
            </ol>
            <VAlert type="error" variant="tonal" density="compact" class="text-body-2">
              <strong>Tidak bisa dipulihkan.</strong> Tombol Hapus hanya muncul untuk PO
              berstatus <strong>Draft</strong> — begitu PO dikirim ke supplier, gunakan
              "Batalkan PO" sebagai gantinya, bukan hapus.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="4">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-close-circle-line" class="me-2" color="error" />
            Batalkan PO
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Gunakan ini jika pesanan tidak jadi dilakukan atau supplier tidak bisa penuhi
              — untuk PO yang <strong>sudah dikirim</strong> (Draft juga boleh).
            </p>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">Buka detail PO</li>
              <li class="mb-2">Klik tombol <strong>"Batalkan PO"</strong> (merah)</li>
              <li>Konfirmasi di dialog yang muncul</li>
            </ol>
            <VAlert type="error" variant="tonal" density="compact" class="text-body-2">
              <strong>PO yang dibatalkan tidak bisa dipulihkan ke Draft.</strong>
              Bedanya dengan Hapus: data PO <strong>tetap tersimpan</strong> sebagai
              riwayat berstatus "Dibatalkan", hanya tidak bisa dipakai untuk receiving.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Isi "Estimasi Tiba" selalu.</strong>
                  Walaupun opsional, tanggal estimasi membantu Anda merencanakan kapan
                  stok akan terisi ulang dan menghindari kehabisan bahan di tengah operasional.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Cek ulang qty sebelum kirim.</strong>
                  Setelah PO dikirim, tidak bisa diedit. Luangkan 30 detik untuk cek ulang
                  jumlah dan harga di setiap item sebelum klik "Kirim ke Supplier".
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Satu PO per supplier per pesanan.</strong>
                  Jika pesan dari 2 supplier berbeda, buat 2 PO terpisah.
                  Ini memudahkan tracking pengiriman dan pembayaran per supplier.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mb-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Next Steps -->
    <VRow>
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Langkah Selanjutnya</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Receiving Barang', sub: 'Catat barang yang datang dari supplier', icon: 'ri-archive-line', color: 'success', to: '/userguide/pembelian/receiving' },
              { title: 'Supplier', sub: 'Tambah atau kelola data supplier', icon: 'ri-store-2-line', color: 'primary', to: '/userguide/pembelian/supplier' },
              { title: 'Laporan Pembelian', sub: 'Rekap semua Purchase Order', icon: 'ri-bar-chart-box-line', color: 'secondary', to: '/userguide/laporan/pembelian' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
